import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

const TAX_RESERVE_PCT = 0.25;

function fmt(n) {
  const v = Number(n || 0);
  return (v < 0 ? "-$" : "$") + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Accounting() {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.hr.list("transactions"), api.hr.list("chart_of_accounts")])
      .then(([t, a]) => {
        setTransactions(t);
        setAccounts(a);
      })
      .catch((e) => setError(e.message));
  }, []);

  const accountType = (code) => accounts.find((a) => a.account_code === code)?.account_type;
  const accountName = (code) => accounts.find((a) => a.account_code === code)?.account_name || code;

  const income = transactions.filter((t) => accountType(t.account_code) === "Income");
  const expenses = transactions.filter((t) => accountType(t.account_code) === "Expense");
  const totalIncome = income.reduce((s, t) => s + Number(t.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0);
  const netProfit = totalIncome - totalExpenses;
  const taxReserve = netProfit > 0 ? netProfit * TAX_RESERVE_PCT : 0;

  const recentIncome = [...income].sort((a, b) => (b.txn_date || "").localeCompare(a.txn_date || "")).slice(0, 5);
  const recentExpenses = [...expenses].sort((a, b) => (b.txn_date || "").localeCompare(a.txn_date || "")).slice(0, 5);
  const needsReview = transactions.filter((t) => t.needs_review === 1 || t.needs_review === "1");

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#154E93" }}>Accounting</h1>
          <p className="text-sm text-gray-500">Income, expenses, and chart of accounts</p>
        </div>
        <div className="space-x-2">
          <Link to="/admin/hr/chart_of_accounts" className="px-4 py-2 rounded text-sm font-medium border" style={{ borderColor: "#154E93", color: "#154E93" }}>
            Chart of Accounts
          </Link>
          <Link to="/admin/hr/transactions" className="px-4 py-2 text-white rounded text-sm font-medium" style={{ backgroundColor: "#154E93" }}>
            Manage Transactions
          </Link>
        </div>
      </div>

      {error && <div className="mb-4 text-sm bg-red-50 text-red-700 px-3 py-2 rounded">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-xs text-gray-500 font-medium uppercase">Total Income</div>
          <div className="text-2xl font-bold mt-1" style={{ color: "#154E93" }}>{fmt(totalIncome)}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-xs text-gray-500 font-medium uppercase">Total Expenses</div>
          <div className="text-2xl font-bold mt-1 text-red-600">{fmt(totalExpenses)}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-xs text-gray-500 font-medium uppercase">Net Profit</div>
          <div className="text-2xl font-bold mt-1" style={{ color: "#154E93" }}>{fmt(netProfit)}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-xs text-gray-500 font-medium uppercase">Tax Reserve ({Math.round(TAX_RESERVE_PCT * 100)}%)</div>
          <div className="text-2xl font-bold mt-1" style={{ color: "#F3850C" }}>{fmt(taxReserve)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="font-semibold mb-3" style={{ color: "#154E93" }}>Recent Income</h3>
          {recentIncome.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
              <div>
                <span className="inline-block bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded mr-2">{t.account_code}</span>
                {accountName(t.account_code)}
              </div>
              <span className="font-medium">{fmt(t.amount)}</span>
            </div>
          ))}
          {recentIncome.length === 0 && <div className="text-sm text-gray-400 py-2">No income recorded yet.</div>}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="font-semibold mb-3" style={{ color: "#154E93" }}>Recent Expenses</h3>
          {recentExpenses.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
              <div>
                <span className="inline-block bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded mr-2">{t.account_code}</span>
                {accountName(t.account_code)}
              </div>
              <span className="font-medium text-red-600">{fmt(Math.abs(t.amount))}</span>
            </div>
          ))}
          {recentExpenses.length === 0 && <div className="text-sm text-gray-400 py-2">No expenses recorded yet.</div>}
        </div>
      </div>

      <h2 className="text-lg font-semibold mb-3" style={{ color: "#154E93" }}>⚠ Items Needing Review</h2>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {needsReview.map((t) => (
          <div key={t.id} className="flex items-center justify-between px-4 py-2 border-b last:border-0 border-l-4 border-orange-400 bg-orange-50 text-sm">
            <div>{t.description || accountName(t.account_code)} — {t.txn_date}</div>
            <span className="font-medium">{fmt(t.amount)}</span>
          </div>
        ))}
        {needsReview.length === 0 && (
          <div className="px-4 py-6 text-center text-gray-400 text-sm">Nothing needs review right now.</div>
        )}
      </div>
    </div>
  );
}
